import { create } from "zustand";
import {
  checkForValidToken,
  loginUser,
  // logoutOnService,
  registerUser,
  resetPasswordService,
  updatePasswordAPI,
  verifyToken,
} from "../services/authService";
import {
  LoginCredentials,
  LoginResponse,
  RegisterUserDetails,
  UpdateUserDetails,
} from "../models/authModels";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { ApiResponseBlank } from "../models/apiResponse";
import { useNotificationsStore } from "./notificationsStore";
import { updateUser } from "../services/usersService";
import {
  sendOtpService,
  verifyOtpService,
} from "../services/verifyEmailService";
import { toast } from "react-toastify";
import { usePlayersStore } from "./playersStore";
import { useSeasonsStore } from "./seasonsStore";
import { useTeamsStore } from "./teamsStore";
import useWebSocketStore from "./webSocketStore";
import { migrate, onRehydrateStorage, storeVersion } from "./versioning";

type AuthStore = {
  version: number;
  message: string;
  currentUser: LoginResponse | null;
  isAuthenticated: boolean;
  isBusyAuthenticating: boolean;
  authState: "notChecked" | "checking" | "valid" | "invalid";
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setIsResetSuccess: (value: boolean) => void;
  setIsSuccess: (value: boolean) => void;
  error: string | null;
  successMessage: string | null;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  isResetSuccess: boolean;
  isSuccess: boolean;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (
    email: string,
    token: string,
    password: string
  ) => Promise<boolean>;
  loginUser: (credentials: LoginCredentials) => Promise<ApiResponseBlank>;
  checkForValidToken: () => Promise<ApiResponseBlank>;
  registerUser: (credentials: RegisterUserDetails) => Promise<ApiResponseBlank>;
  logout: () => void;
  updateUser: (user: UpdateUserDetails) => void;
  verifyToken: () => Promise<void>;
};
const defaultAuthStoreData = {
  error: null,
  successMessage: null,
  message: "",
  currentUser: null,
  isResetSuccess: false,
  isSuccess: false,
  isAuthenticated: false,
  isBusyAuthenticating: false,
  authState: "notChecked" as const,
};

const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...defaultAuthStoreData,
        version: storeVersion,
        setIsAuthenticated: (isAuthenticated: boolean) =>
          set({ isAuthenticated }),

        loginUser: async (
          credentials: LoginCredentials
        ): Promise<ApiResponseBlank> => {
          set({ isBusyAuthenticating: true, authState: "checking" });
          try {
            const response = await loginUser(credentials);
            if (response.result as LoginResponse) {
              set({
                currentUser: response.result,
                isAuthenticated: true,
                authState: "valid",
              });
              if (response.result) {
                useWebSocketStore
                  .getState()
                  .connectWebSocket(response.result.email);
              }
              useNotificationsStore.getState().getAllNotifications();
            } else {
              set({
                isAuthenticated: false,
                authState: "invalid",
                CurrentUser: null,
              });
            }
            set({ isBusyAuthenticating: false });

            return { code: response.code, message: response.message };
          } catch {
            set({
              isAuthenticated: false,
              authState: "invalid",
              isBusyAuthenticating: false,
            });
            return {
              code: 1000,
              message:
                "Unable to login at the moment! Please check you network connection or try again later.",
            };
          }
        },
        checkForValidToken: async () => {
          set({ isBusyAuthenticating: true, authState: "checking" });

          const response = await checkForValidToken();
          if (response.code === 1004) {
            set({
              isAuthenticated: true,
              currentUser: response.result,
              authState: "valid",
            });
          } else {
            set({
              isAuthenticated: false,
              authState: "invalid",
              CurrentUser: null,
            });
          }
          set({ isBusyAuthenticating: false });

          return response;
        },
        registerUser: async (credentials: RegisterUserDetails) => {
          set({ isBusyAuthenticating: true, authState: "checking" });
          try {
            const response = await registerUser(credentials);
            if (response.code === 1001) {
              set({
                currentUser: response.result,
                isAuthenticated: true,
                authState: "valid",
                isBusyAuthenticating: false,
              });
              return { code: response.code, message: response.message };
            } else if (response.code === 1002 || response.code === 1000) {
              set({
                isAuthenticated: false,
                authState: "invalid",
                message: response.message,
                isBusyAuthenticating: false,
              });
            } else {
              set({
                isAuthenticated: false,
                authState: "invalid",
                isBusyAuthenticating: false,
              });
            }
            return { code: response.code, message: response.message };
          } catch {
            set({
              isAuthenticated: false,
              authState: "invalid",
              isBusyAuthenticating: false,
            });
            return {
              code: 999,
              message:
                "An error occurred when trying to register. Please try again later.",
            };
          }
        },
        logout: () => {
          set({
            currentUser: null,
            isAuthenticated: false,
            authState: "invalid",
            isBusyAuthenticating: false,
          });
          useAuthStore.persist.clearStorage();
          usePlayersStore.persist.clearStorage();
          useSeasonsStore.persist.clearStorage();
          useTeamsStore.persist.clearStorage();
          try {
            document.cookie.split(";").forEach((c) => {
              document.cookie = c
                .replace(/^ +/, "")
                .replace(
                  /=.*/,
                  "=;expires=" + new Date().toUTCString() + ";path=/"
                );
            });
          } catch (error) {
            console.error("Failed to clear cookies:", error);
          }

          // logoutOnService();
        },
        updateUser: async (user: any) => {
          const u = get().currentUser;
          const updatedUser = { ...u, ...user };
          set({ currentUser: updatedUser as LoginResponse });
          const response = await updateUser(updatedUser);
          if (response.code === 1013) {
            set({ currentUser: { ...u, ...response.result } });
          }
        },
        sendOtp: async (email: string) => {
          set({
            isBusyAuthenticating: true,
            error: null,
            successMessage: null,
          });
          try {
            await sendOtpService(email);
            set({
              successMessage: "Please enter the verification code here",
              isBusyAuthenticating: false,
            });
          } catch (error) {
            set({ error: "Failed to send OTP", isBusyAuthenticating: false });
          }
        },

        verifyOtp: async (email: string, otp: string) => {
          set({
            isBusyAuthenticating: true,
            error: null,
            successMessage: null,
          });
          try {
            const response = await verifyOtpService(email, otp);
            if (response.code === 9069) {
              set({
                successMessage: "Verified successfully",
                isBusyAuthenticating: false,
              });
              toast.success("Verified successfully");
              get().checkForValidToken();
            } else if (response.code === 9067) {
              set({
                error: "Invalid OTP. Please try again.",
                isBusyAuthenticating: false,
              });
            } else if (response.code === 9068) {
              set({
                error: "OTP Expired. Please resend OTP.",
                isBusyAuthenticating: false,
              });
            }
          } catch (error) {
            set({ error: "Failed to verify OTP", isBusyAuthenticating: false });
            toast.error("Failed to verify OTP");
          }
        },
        resetPassword: async (email: string) => {
          set({
            isBusyAuthenticating: true,
            error: null,
            successMessage: null,
          });
          try {
            const response = await resetPasswordService(email);
            if (response.code === 9090) {
              set({
                successMessage: response.message,
                isBusyAuthenticating: false,
                isResetSuccess: true,
              });
            } else {
              set({
                error: response.message,
                isBusyAuthenticating: false,
              });
              toast.error(response.message);
            }
          } catch (error) {
            console.error("Unexpected error in resetPassword:", error);
            set({
              error: "An unexpected error occurred. Please try again later.",
              isBusyAuthenticating: false,
            });
          }
        },
        setIsResetSuccess: (value: boolean) => set({ isResetSuccess: value }),
        updatePassword: async (
          token: string,
          newPassword: string,
          email: string
        ) => {
          set({
            isBusyAuthenticating: true,
            error: null,
            successMessage: null,
          });
          try {
            const response = await updatePasswordAPI(token, newPassword, email);
            if (response.code === 9087) {
              set({
                successMessage: response.message,
                isBusyAuthenticating: false,
                isSuccess: true,
              });
            } else {
              set({
                error: response.message,
                isBusyAuthenticating: false,
              });
              toast.error(response.message);
              return false;
            }
            return true;
          } catch (error) {
            console.error("Error updating password:", error);
            set({
              error: "An unexpected error occurred. Please try again later.",
              isBusyAuthenticating: false,
            });
            toast.error(
              "An unexpected error occurred. Please try again later."
            );
            return false;
          }
        },
        setIsSuccess: (value: boolean) => set({ isSuccess: value }),
        verifyToken: async () => {
          set({ isBusyAuthenticating: true, authState: "checking" });
          const currentUser = get().currentUser as LoginResponse;
          if (!currentUser || !currentUser.tokens) {
            set({
              isAuthenticated: false,
              currentUser: null,
              authState: "invalid",
            });
            set({ isBusyAuthenticating: false });
            return;
          }

          const response = await verifyToken();
          if (response.code === 1004) {
            set({
              isAuthenticated: true,
              currentUser: response.result,
              authState: "valid",
            });
          } else {
            set({
              isAuthenticated: false,
              currentUser: null,
              authState: "invalid",
            });
          }
          set({ isBusyAuthenticating: false });
        },
      }),
      {
        name: "authStore",
        version: 1,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          currentUser: state.currentUser,
          isAuthenticated: state.isAuthenticated,
        }),
        migrate: migrate(defaultAuthStoreData),
        onRehydrateStorage: onRehydrateStorage(defaultAuthStoreData),
      }
    ),
    { name: "authStore", enabled: process.env.NODE_ENV === "development" }
  )
);

export default useAuthStore;
